import {
  DeleteOutlined,
  EditOutlined,
  MenuOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface FAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
  eventId: string;
  Event?: {
    id: string;
    title: string;
    Organization?: {
      name: string;
    };
  };
}

interface Event {
  id: string;
  title: string;
  organizationId: string;
}

const SortableRow = ({ children, ...props }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort') {
          return React.cloneElement(child as React.ReactElement<{ children?: React.ReactNode }>, {
            children: (
              <MenuOutlined
                style={{ cursor: 'move', color: '#999' }}
                {...attributes}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const FAQs: React.FC = () => {
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const [form] = Form.useForm();
  const { user } = useAuthStore();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchEvents();
    fetchFaqs();
  }, []);

  const fetchEvents = async () => {
    try {
      const params: any = {};
      if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }
      const response = await api.get('/events', { params });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedEvent) {
        params.eventId = selectedEvent;
      } else if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }

      const response = await api.get('/faqs', { params });
      setFaqs(response.data || []);
    } catch (error) {
      message.error(t('faqs.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, [selectedEvent]);

  const handleCreate = () => {
    setEditingFaq(null);
    form.resetFields();
    const maxOrder = Math.max(...faqs.map((f) => f.order), -1);
    form.setFieldsValue({ order: maxOrder + 1 });
    setModalVisible(true);
  };

  const handleEdit = (record: FAQ) => {
    setEditingFaq(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/faqs/${id}`);
      message.success(t('faqs.deleted'));
      fetchFaqs();
    } catch (error) {
      message.error(t('messages.delete_error'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingFaq) {
        // Remove eventId from update data as it's not allowed in UpdateFaqDto
        const { eventId, ...updateData } = values;
        await api.patch(`/faqs/${editingFaq.id}`, updateData);
        message.success(t('faqs.updated'));
      } else {
        await api.post('/faqs', values);
        message.success(t('faqs.created'));
      }
      setModalVisible(false);
      fetchFaqs();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('faqs.operationFailed'));
    }
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = faqs.findIndex((faq) => faq.id === active.id);
      const newIndex = faqs.findIndex((faq) => faq.id === over.id);

      const newFaqs = arrayMove(faqs, oldIndex, newIndex);
      setFaqs(newFaqs);

      if (selectedEvent) {
        try {
          await api.post(`/faqs/reorder/${selectedEvent}`, {
            faqIds: newFaqs.map((f) => f.id),
          });
          message.success(t('faqs.orderUpdated'));
        } catch (error) {
          message.error(t('faqs.orderUpdateFailed'));
          fetchFaqs();
        }
      }
    }
  };

  const columns: ColumnsType<FAQ> = [
    {
      key: 'sort',
      width: 30,
      render: () => null,
    },
    {
      title: '#',
      dataIndex: 'order',
      key: 'order',
      width: 60,
    },
    {
      title: t('faqs.question'),
      dataIndex: 'question',
      key: 'question',
      render: (question: string) => (
        <Space>
          <QuestionCircleOutlined />
          {question}
        </Space>
      ),
    },
    {
      title: t('faqs.answer'),
      dataIndex: 'answer',
      key: 'answer',
      ellipsis: true,
    },
    {
      title: t('common.event'),
      dataIndex: ['Event', 'title'],
      key: 'event',
      render: (title: string) => title || '-',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>{t('faqs.title')}</Title>
          <Space>
            <Select
              style={{ width: 250 }}
              placeholder={t('common.allEvents')}
              allowClear
              value={selectedEvent}
              onChange={setSelectedEvent}
            >
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('faqs.new_faq')}
            </Button>
          </Space>
        </div>

        {selectedEvent ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={faqs.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <Table
                components={{
                  body: {
                    row: SortableRow,
                  },
                }}
                columns={columns}
                dataSource={faqs}
                rowKey="id"
                loading={loading}
                pagination={false}
              />
            </SortableContext>
          </DndContext>
        ) : (
          <Table
            columns={columns.filter((col) => col.key !== 'sort')}
            dataSource={faqs}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => t('faqs.paginationTotal', { count: total }),
            }}
          />
        )}
      </Space>

      <Modal
        title={editingFaq ? t('faqs.edit_faq') : t('faqs.new_faq')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="eventId"
            label={t('common.event')}
            rules={[{ required: true, message: t('common.selectEventRequired') }]}
          >
            <Select placeholder={t('common.selectEvent')} disabled={!!editingFaq}>
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="question"
            label={t('faqs.question')}
            rules={[{ required: true, message: t('faqs.questionRequired') }]}
          >
            <Input placeholder={t('faqs.questionPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="answer"
            label={t('faqs.answer')}
            rules={[{ required: true, message: t('faqs.answerRequired') }]}
          >
            <TextArea rows={4} placeholder={t('faqs.answerPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="order"
            label={t('faqs.order')}
            rules={[{ required: true, message: t('faqs.orderRequired') }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingFaq ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('faqs.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default FAQs;
